import React, { useRef, useState, useEffect, useCallback } from "react";
import { useTranslation, Trans } from "react-i18next";
import { CelebrationButtonOutline } from "./CelebrationButtonOutline";
import { CheckmarkIcon } from "@/assets/svg/CheckmarkIcon";
import { ConfettiAnimation } from "@/components/Congrats/ConfettiAnimation";
import { SingleTaskProgress } from "./SingleTaskProgress";
import { TeamTaskProgress } from "./TeamTaskProgress";
import { getDateString } from "@/util/dateUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/Dialog";

export enum TaskProgressView {
  Single = "single",
  Team = "team",
}

interface UserProgress {
  id: string;
  name: string;
  profileUrl: string;
  completed: number;
  skipped: number;
  total: number;
}

interface TaskProgressProps {
  users: UserProgress[];
  view: TaskProgressView;
}

const CONGRATS_STORAGE_KEY = "taskProgressCongratsShown";

const hasShownCongratsToday = () => {
  const stored = localStorage.getItem(CONGRATS_STORAGE_KEY);
  if (!stored) return false;
  return stored === getDateString(new Date());
};

const markCongratsShownToday = () => {
  localStorage.setItem(CONGRATS_STORAGE_KEY, getDateString(new Date()));
};

const shouldAutoShowCongrats = (
  hasShownCongratsRef: React.RefObject<boolean>,
) => {
  return !hasShownCongratsToday() && !hasShownCongratsRef.current;
};

const calculateAggregateProgress = (users: UserProgress[]): number => {
  if (users.length === 0) return 0;
  const totalCompleted = users.reduce(
    (sum, user) => sum + user.completed + user.skipped,
    0,
  );
  const totalTasks = users.reduce((sum, user) => sum + user.total, 0);
  if (totalTasks === 0) return 0;
  const progress = Math.round((totalCompleted / totalTasks) * 100);
  return Math.min(progress, 100);
};

export const TaskProgress: React.FC<TaskProgressProps> = ({ users, view }) => {
  const { t } = useTranslation();

  const [congratsDialogOpen, setCongratsDialogOpen] = useState(false);
  const [fireConfetti, setFireConfetti] = useState(false);
  const congratsDialogRef = useRef<HTMLDivElement>(null);
  const previousProgressRef = useRef<number>(0);
  const previousUsersAt100Ref = useRef<Set<string>>(new Set());
  const hasShownCongratsRef = useRef(false);

  useEffect(() => {
    if (congratsDialogOpen && !fireConfetti) {
      requestAnimationFrame(() => {
        setFireConfetti(true);
      });
    } else if (!congratsDialogOpen && fireConfetti) {
      setFireConfetti(false);
    }
  }, [congratsDialogOpen, fireConfetti]);

  const calculateProgress = useCallback((user: UserProgress): number => {
    if (user.total === 0) return 0;
    const progress = Math.round(
      ((user.completed + user.skipped) / user.total) * 100,
    );
    return Math.min(progress, 100);
  }, []);

  const currentProgress =
    view === TaskProgressView.Single
      ? users.length > 0
        ? calculateProgress(users[0])
        : 0
      : calculateAggregateProgress(users);

  const celebrationEnabled =
    view === TaskProgressView.Single
      ? currentProgress >= 100
      : users.some((user) => calculateProgress(user) >= 100);

  const celebrationBorderColor = celebrationEnabled
    ? "yellow-600"
    : "yellow-300";

  const usersProgressKey = users
    .map((u) => `${u.id}-${u.completed}-${u.skipped}-${u.total}`)
    .join(",");

  useEffect(() => {
    if (
      view === TaskProgressView.Single &&
      shouldAutoShowCongrats(hasShownCongratsRef)
    ) {
      const wasBelow100 = previousProgressRef.current < 100;
      const isNow100 = currentProgress >= 100;

      if (isNow100 && (previousProgressRef.current === 0 || wasBelow100)) {
        setCongratsDialogOpen(true);
        hasShownCongratsRef.current = true;
        markCongratsShownToday();
      }

      previousProgressRef.current = currentProgress;
    } else if (
      view === TaskProgressView.Team &&
      shouldAutoShowCongrats(hasShownCongratsRef)
    ) {
      const currentUsersAt100 = new Set(
        users
          .filter((user) => calculateProgress(user) >= 100)
          .map((user) => user.id),
      );

      const newUsersAt100 = Array.from(currentUsersAt100).filter(
        (id) => !previousUsersAt100Ref.current.has(id),
      );

      if (newUsersAt100.length > 0) {
        setCongratsDialogOpen(true);
        hasShownCongratsRef.current = true;
        markCongratsShownToday();
      }

      previousUsersAt100Ref.current = currentUsersAt100;
    }
  }, [currentProgress, view, usersProgressKey, users, calculateProgress]);

  const titleKey =
    view === TaskProgressView.Single
      ? "strategyBoard.progress.single.title"
      : "strategyBoard.progress.team.title";

  const progressLabelKey =
    view === TaskProgressView.Single
      ? "strategyBoard.progress.single.currentProgress"
      : "strategyBoard.progress.team.currentProgress";

  return (
    <>
      <ConfettiAnimation
        containerRef={congratsDialogRef}
        trigger={fireConfetti}
      />
      <div className="w-full rounded-xl border border-border p-6 pt-3 pr-3 pb-2">
        <div className="mb-[2.5px] flex items-center justify-between">
          <div className="text-sm leading-4 font-bold text-foreground">
            {t(titleKey)}
          </div>
          <div className="flex items-center gap-3.5">
            <div className="flex items-center gap-1.5">
              <span className="text-sm leading-5 text-foreground">
                {t(progressLabelKey)}
              </span>
              <span className="text-sm leading-5 font-bold text-foreground">
                {currentProgress}%
              </span>
            </div>
            <div className="pt-[5px]">
              <div
                className="relative flex h-[39px] w-[34px] cursor-pointer items-center justify-center"
                onClick={() => {
                  if (celebrationEnabled) {
                    setCongratsDialogOpen(true);
                  }
                }}
              >
                <CelebrationButtonOutline
                  borderColor={celebrationBorderColor}
                  fillColor={celebrationEnabled ? "yellow-100" : "yellow-50"}
                />
                <span className="absolute z-10 -mt-1 text-xl leading-5 font-bold">
                  🎉
                </span>
              </div>
            </div>
          </div>
        </div>

        {view === TaskProgressView.Single && users.length > 0 ? (
          <SingleTaskProgress
            user={users[0]}
            currentProgress={currentProgress}
          />
        ) : (
          <TeamTaskProgress
            users={users}
            calculateProgress={calculateProgress}
          />
        )}
      </div>

      <Dialog open={congratsDialogOpen} onOpenChange={setCongratsDialogOpen}>
        <DialogContent
          ref={congratsDialogRef}
          className="max-w-sm border-none p-8 shadow-xl"
        >
          <DialogHeader>
            <DialogTitle className="sr-only">
              {t("strategyBoard.progress.congrats.title")}
            </DialogTitle>
            <DialogDescription className="sr-only"></DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-6">
            <CheckmarkIcon className="h-16 w-16" />
            <h2 className="text-center text-2xl leading-8 font-bold text-foreground">
              <Trans
                i18nKey="strategyBoard.progress.congrats.title"
                components={{ br: <br /> }}
              />
            </h2>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
